import { Cell, MarkdownCell } from "@jupyterlab/cells";
import { CodeMirrorEditor } from "@jupyterlab/codemirror";
import { mirrorMark } from "./mirrorMark/mirrorMark";

/**
 * How quick a double click needs to be performed.
 *
 * Windows default is 500ms, but for accessibility going a bit higher.
 */
const DOUBLE_CLICK_THRESHOLD_MS = 700;

enum KeyCode {
  ESC = 27,
}

class MarkdownToolbar {
  private activeCell?: Cell;
  private toolbar?: HTMLElement;
  private lastCellClickTime?: number;

  constructor() {
    // Note: this is not possible from "keypress" since Jupyter stops propagation.
    document.addEventListener("keydown", this.keyDownHandler);
  }

  /** Handles key down events that should cause markdown cells to change state (ESC, etc.) */
  private keyDownHandler = (e: KeyboardEvent) => {
    switch (e.keyCode) {
      case KeyCode.ESC:
        // Don't call handleCellChange here since it will cause loss of activeCell state (the cell is still active).
        if (this.activeCell) {
          this.removeToolbar();
          this.renderActiveCell();
        }
    }
  };

  /** When cell is rendered, if markdown switch to edit mode. */
  handleCellActive = (cell: Cell) => {
    const isSameCell = cell === this.activeCell;
    if (!isSameCell) {
      this.handleCellChange(cell);
    }

    const isDoubleClick =
      isSameCell &&
      Date.now() - (this.lastCellClickTime || 0) < DOUBLE_CLICK_THRESHOLD_MS;
    if (isDoubleClick && cell?.model?.type === "markdown") {
      const markdownCell = cell as MarkdownCell;
      markdownCell.rendered = false;

      // This is required to inject the toolbar immediately, otherwise it won't occur until the cell is clicked on.
      this.handleEditCell(cell);
    }

    this.lastCellClickTime = Date.now();
  };

  /** Render the previous markdown cell when new cell clicked. */
  private handleCellChange = (cell?: Cell) => {
    if (
      this.activeCell &&
      this.activeCell !== cell &&
      this.activeCell.model?.type === "markdown"
    ) {
      this.removeToolbar();
      this.renderActiveCell();
    }
    this.activeCell = cell;
  };

  /** Register mirror mark for markdown cells on edit mode switch. */
  handleEditCell = (cell: Cell) => {
    if (cell?.model?.type === "markdown" && !this.toolbar) {
      const editor = mirrorMark();

      const cmEditor = cell.editor as CodeMirrorEditor;
      this.toolbar = editor.render(cmEditor.editor);
    }
  };

  /** Removes the mirrormark toolbar if it exists. */
  private removeToolbar = () => {
    if (this.toolbar) {
      this.toolbar.remove();
      this.toolbar = undefined;
    }
  };

  /** Renders the currently active cell if its markdown. */
  private renderActiveCell = () => {
    if (this.activeCell && this.activeCell.model?.type === "markdown") {
      // Render the prior markdown cell.
      const markdownCell = this.activeCell as MarkdownCell;
      markdownCell.rendered = true;
    }
  };
}

export const markdownToolbarJupyterLab = new MarkdownToolbar();
